import { clamp } from "../../../util/functions/clamp";
import { sortTopThenLeft } from "../../../util/functions/sort";
import {
	CoordinateSpace,
	type ICoordinateSpace,
	type InFullSizeImage,
	type InOverviewImage,
	type InTransformed,
	type InVisibleCanvas,
	type InVisibleCanvasDrawRegion,
	type InWindow,
} from "./CoordinateSpace";
import { type IPoint, Point, PointInFullSizeImage, PointInOverviewImage, PointInTransformedFullSizeImage, PointInWindow } from "./Point";
import type { ISize } from "./Size";
import type { ITransformation } from "./Transformation";
import type { IVector } from "./Vector";
import type { BasicRegion } from "./BasicRegion";
import type { IVisibleCanvasStore } from "../viewports/viewport-series/visible-canvas/VisibleCanvasStore";
import { ZoomDirection } from "./ZoomDirection";
import type { ZoomFactor } from "../viewports/viewport-series/zoom/ZoomFactor";

export interface IRegion<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> {
	readonly type: "region";
	readonly kind: string;
	readonly coordinateSpace: TCoordinateSpace;

	/**
        Coordinates of the four points that make up this region 
    **/
	readonly topLeft: IPoint<TCoordinateSpace>;
	readonly topRight: IPoint<TCoordinateSpace>;
	readonly bottomLeft: IPoint<TCoordinateSpace>;
	readonly bottomRight: IPoint<TCoordinateSpace>;

	/** 
        Derived information
    **/
	readonly center: IPoint<TCoordinateSpace>;
	readonly size: ISize<TCoordinateSpace>;

	/**
        The bounding region to which this region is constrained.
        This region can never grow bigger than its bounds, and its coordinates must always stay within the boundary points
    **/
	readonly topLeftBoundary: IPoint<TCoordinateSpace>;
	readonly topRightBoundary: IPoint<TCoordinateSpace>;
	readonly bottomLeftBoundary: IPoint<TCoordinateSpace>;
	readonly bottomRightBoundary: IPoint<TCoordinateSpace>;
	readonly maxSize: ISize<TCoordinateSpace>;

	/**
	 * Returns whether or not the provided point belongs to this region
	 */
	contains(point: IPoint<TCoordinateSpace>): boolean;

	/**
	 * Will try to increase/decrease the size of this region while nudging towards the provided center, or the existing center if no center is provided
	 *
	 * @param direction The direction in which to zoom. Can be 'In' or 'Out'
	 * @param factor A number 0 < x < 1 that decides how far to zoom in/out. 0 or 1 are not allowed and will result in no changes.
	 * @param center (Optional) The point that should be kept visible while zooming. Conceptually, this is the point that should still be visible when zooming in 1000x.
	 *  When zooming out, the resulting region width and height will be factor * width and factor * height respectively.
	 *  When zooming in, the resulting region width and height will be width/(1-factor) and height/(1-factor) respectively.
	 */
	zoom(direction: ZoomDirection, factor: ZoomFactor, center?: IPoint<TCoordinateSpace>): IRegion<TCoordinateSpace>;

	/**
	 * Will try to change the size of this region to the requested size, staying around the existing center if possible
	 * If, due to the resize, any bounds are overflowed - topLeft cannot go beyond (0,0) and bottomRight cannot go beyond (maxSize.width, maxSize.height) -
	 * this region will automatically move to respect the boundaries, modifying its center until no boundaries are overflown.
	 *
	 * If there is no move possible that respects the boundaries, this region will automatically crop itself until the boundaries are respected.
	 */
	zoomTo(size: ISize<TCoordinateSpace>): IRegion<TCoordinateSpace>;

	/**
	 * Moves this region, but never outside the boundaries, always maintaining the current size.
	 */
	move(vector: IVector<TCoordinateSpace>, options?: { disableRounding?: boolean }): IRegion<TCoordinateSpace>;

	/**
	 * Resized (and might move) this region, but never outside the boundaries, tries to maintain the current topLeft position.
	 * ⚠ Resizing might move the region.
	 * It first tries to keep the topLeft position, then resizes.
	 * If the borderRight boundaries are hit, and we can still resize by moving the topLeft, we'll move the topLeft
	 */
	resize(vector: IVector<TCoordinateSpace>): IRegion<TCoordinateSpace>;

	/**
	 * Resize this region (but never move!!), but never outside the boundaries. Maintains the current center position
	 * ⚠ This WILL NOT move the region!
	 */
	resizeThroughCenter(vector: IVector<TCoordinateSpace>): IRegion<TCoordinateSpace>;

	/**
	 * Rounds the coordinate points of this region upwards while respecting the region boundaries
	 */
	ceil(): IRegion<TCoordinateSpace>;

	equals(other: IRegion<TCoordinateSpace>): boolean;

	/**
	 * transforms the current region to a new region in the transformed coordinate space.
	 */
	transform(transformation: ITransformation<TCoordinateSpace>): IRegion<InTransformed<TCoordinateSpace>>;

	/**
	 * Creates a BasicRegion that can be used to transfer data to a web worker / serialize to JSON / ...
	 */
	toBasicRegion(): BasicRegion;

	toString(): string;
}

export abstract class Region<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> implements IRegion<TCoordinateSpace> {
	/* lazily computed fields */
	private _size: ISize<TCoordinateSpace> | null = null;
	private _topRight: IPoint<TCoordinateSpace> | null = null;
	private _bottomLeft: IPoint<TCoordinateSpace> | null = null;
	private _center: IPoint<TCoordinateSpace> | null = null;
	private _maxSize: ISize<any> | null = null;

	public readonly topLeftBoundary: IPoint<TCoordinateSpace>;
	public readonly bottomRightBoundary: IPoint<TCoordinateSpace>;

	public readonly coordinateSpace: TCoordinateSpace;
	public readonly topLeft: IPoint<TCoordinateSpace>;
	public readonly bottomRight: IPoint<TCoordinateSpace>;

	public abstract readonly kind: string;
	public readonly type: "region";

	protected constructor(
		coordinateSpace: TCoordinateSpace,
		topLeft: IPoint<TCoordinateSpace>,
		bottomRight: IPoint<TCoordinateSpace>,
		topLeftBoundary: IPoint<TCoordinateSpace>,
		bottomRightBoundary: IPoint<TCoordinateSpace>,
	) {
		this.type = "region";
		this.coordinateSpace = coordinateSpace;
		this.topLeft = topLeft;
		this.bottomRight = bottomRight;
		this.topLeftBoundary = topLeftBoundary;
		this.bottomRightBoundary = bottomRightBoundary;
		this.validate();
	}

	public get size() {
		return this._size || (this._size = this.coordinateSpace.createSize(this.bottomRight.x - this.topLeft.x, this.bottomRight.y - this.topLeft.y));
	}

	public get topRight() {
		return this._topRight || (this._topRight = this.coordinateSpace.createPoint(this.bottomRight.x, this.topLeft.y));
	}

	public get bottomLeft() {
		return this._bottomLeft || (this._bottomLeft = this.coordinateSpace.createPoint(this.topLeft.x, this.bottomRight.y));
	}

	public get center() {
		return this._center || (this._center = this.topLeft.average(this.bottomRight));
	}

	public get maxSize() {
		return (
			this._maxSize || (this._maxSize = this.coordinateSpace.createSize(this.bottomRightBoundary.x - this.topLeftBoundary.x, this.bottomRightBoundary.y - this.topLeftBoundary.y))
		);
	}

	public get topRightBoundary() {
		return this.coordinateSpace.createPoint(this.bottomRightBoundary.x, this.topLeftBoundary.y);
	}

	public get bottomLeftBoundary() {
		return this.coordinateSpace.createPoint(this.topLeftBoundary.x, this.bottomRightBoundary.y);
	}

	contains(point: IPoint<TCoordinateSpace>): boolean {
		return point.x >= this.topLeft.x && point.y >= this.topLeft.y && point.x <= this.bottomRight.x && point.y <= this.bottomRight.y;
	}

	zoomTo(size: ISize<TCoordinateSpace>): IRegion<TCoordinateSpace> {
		const { topLeftBoundary, bottomRightBoundary, center, maxSize } = this;
		const { width: maxWidth, height: maxHeight } = maxSize;

		// halfwit :-)
		const halfWidth = clamp(size.width, 0, maxWidth) / 2;
		const halfHeight = clamp(size.height, 0, maxHeight) / 2;

		const newTopLeftX = center.x - halfWidth;
		const newTopLeftY = center.y - halfHeight;

		const newBottomRightX = center.x + halfWidth;
		const newBottomRightY = center.y + halfHeight;

		// when the top left or bottom right exceed boundaries, we will try to shift the center
		const overflowTopLeftX = newTopLeftX < topLeftBoundary.x ? topLeftBoundary.x - newTopLeftX : 0;
		const overflowTopLeftY = newTopLeftY < topLeftBoundary.y ? topLeftBoundary.y - newTopLeftY : 0;
		const overflowBottomRightX = newBottomRightX > bottomRightBoundary.x ? bottomRightBoundary.x - newBottomRightX : 0;
		const overflowBottomRightY = newBottomRightY > bottomRightBoundary.y ? bottomRightBoundary.y - newBottomRightY : 0;

		const topLeft = this.coordinateSpace.createPoint(
			clamp(newTopLeftX + overflowBottomRightX, topLeftBoundary.x, bottomRightBoundary.x),
			clamp(newTopLeftY + overflowBottomRightY, topLeftBoundary.y, bottomRightBoundary.y),
		);

		const bottomRight = this.coordinateSpace.createPoint(
			clamp(newBottomRightX + overflowTopLeftX, topLeft.x, bottomRightBoundary.x),
			clamp(newBottomRightY + overflowTopLeftY, topLeft.y, bottomRightBoundary.y),
		);

		return this.coordinateSpace.createRegion(topLeft, bottomRight, topLeftBoundary, bottomRightBoundary);
	}

	zoom(direction: ZoomDirection, factor: ZoomFactor, center?: IPoint<TCoordinateSpace>): IRegion<TCoordinateSpace> {
		const { maxSize, size, topLeft, bottomRight, topLeftBoundary, bottomRightBoundary } = this;
		const { width, height } = size;
		const { width: maxWidth, height: maxHeight } = maxSize;

		if (direction === ZoomDirection.In && (width <= 50 || height <= 50)) {
			console.log("🔎 Cannot zoom in any further");
			return this;
		}
		if (direction === ZoomDirection.Out && width >= maxWidth && height >= maxHeight) {
			console.log("🔎 Cannot zoom out any further");
			return this;
		}
		if (factor.value === 1) {
			console.log("🔎 Tried to zoom in with zoom factor 1");
			return this;
		}

		// calculate the desired new width/height after zooming
		// when zooming in, the region will shrink and deltaWidth will be negative.
		// when zooming out, the region will grow, so deltaWidth will be positive
		const deltaWidth = direction * Math.max(width * factor.value, 1);
		const deltaHeight = direction * Math.max(height * factor.value, 1);

		// now we must 'divide' the delta width, taking into account the zoom center
		// if the center is all the way to the left, the extra width will be given to the right side
		// if the center is all the way to the top, the extra height will be given to the bottom side
		// etc.
		const zoomCenter = center ?? this.center;
		const distributionWeightLeftOfZoomCenter = (zoomCenter.x - topLeft.x) / width;
		const distributionWeightAboveZoomCenter = (zoomCenter.y - topLeft.y) / height;
		const deltaWidthLeftOfZoomCenter = deltaWidth * distributionWeightLeftOfZoomCenter;
		const deltaWidthRightOfZoomCenter = deltaWidth - deltaWidthLeftOfZoomCenter;

		const deltaHeightAboveZoomCenter = deltaHeight * distributionWeightAboveZoomCenter;
		const deltaHeightBelowZoomCenter = deltaHeight - deltaHeightAboveZoomCenter;

		const newTopLeftX = topLeft.x - deltaWidthLeftOfZoomCenter;
		const newTopLeftY = topLeft.y - deltaHeightAboveZoomCenter;

		const newBottomRightX = Math.max(bottomRight.x + deltaWidthRightOfZoomCenter, newTopLeftX + 1);
		const newBottomRightY = Math.max(bottomRight.y + deltaHeightBelowZoomCenter, newTopLeftY + 1);

		// when the top left or bottom right exceed boundaries, we will try to shift the center
		const overflowTopLeftX = newTopLeftX < topLeftBoundary.x ? topLeftBoundary.x - newTopLeftX : 0;
		const overflowTopLeftY = newTopLeftY < topLeftBoundary.y ? topLeftBoundary.y - newTopLeftY : 0;
		const overflowBottomRightX = newBottomRightX > bottomRightBoundary.x ? bottomRightBoundary.x - newBottomRightX : 0;
		const overflowBottomRightY = newBottomRightY > bottomRightBoundary.y ? bottomRightBoundary.y - newBottomRightY : 0;

		const newTopLeft = this.coordinateSpace.createPoint(
			clamp(newTopLeftX + overflowBottomRightX, topLeftBoundary.x, bottomRightBoundary.x),
			clamp(newTopLeftY + overflowBottomRightY, topLeftBoundary.y, bottomRightBoundary.y),
		);

		const newBottomRight = this.coordinateSpace.createPoint(
			clamp(newBottomRightX + overflowTopLeftX, newTopLeft.x, bottomRightBoundary.x),
			clamp(newBottomRightY + overflowTopLeftY, newTopLeft.y, bottomRightBoundary.y),
		);

		return this.coordinateSpace.createRegion(newTopLeft, newBottomRight, topLeftBoundary, bottomRightBoundary);
	}

	move(vector: IVector<TCoordinateSpace>, options?: { disableRounding?: boolean }): IRegion<TCoordinateSpace> {
		let { x, y } = vector;

		if (options?.disableRounding !== true) {
			x = Math.round(x);
			y = Math.round(y);
		}

		if (x === 0 && y === 0) return this as any as IRegion<TCoordinateSpace>;

		const { topLeft, bottomRight, size, topLeftBoundary, bottomRightBoundary } = this;

		const clampedVector = this.coordinateSpace.createVector(
			clamp(x, topLeftBoundary.x - topLeft.x, bottomRightBoundary.x - size.width - topLeft.x),
			clamp(y, topLeftBoundary.y - topLeft.y, bottomRightBoundary.y - size.height - topLeft.y),
		);

		const newTopLeft = topLeft.move(clampedVector);
		const newBottomRight = bottomRight.move(clampedVector);

		return this.coordinateSpace.createRegion(newTopLeft, newBottomRight, topLeftBoundary, bottomRightBoundary);
	}

	resize(vector: IVector<TCoordinateSpace>): IRegion<TCoordinateSpace> {
		if (vector.x === 0 && vector.y === 0) return this as any as IRegion<TCoordinateSpace>;

		const { topLeft, bottomRight, size, topLeftBoundary, bottomRightBoundary } = this;

		const clampedBottomRightVector = this.coordinateSpace.createVector(
			clamp(vector.x, -size.width, bottomRightBoundary.x - bottomRight.x),
			clamp(vector.y, -size.height, bottomRightBoundary.y - bottomRight.y),
		);

		let newTopLeft = topLeft;
		const newBottomRight = bottomRight.move(clampedBottomRightVector);

		const diffVector = clampedBottomRightVector.subtract(vector);
		if (clampedBottomRightVector.x < vector.x || clampedBottomRightVector.y < vector.y) {
			const clampedTopLeftVector = this.coordinateSpace.createVector(clamp(diffVector.x, topLeftBoundary.x - topLeft.x, 0), clamp(diffVector.y, topLeftBoundary.y - topLeft.y, 0));

			newTopLeft = topLeft.move(clampedTopLeftVector);
		}

		return this.coordinateSpace.createRegion(newTopLeft, newBottomRight, topLeftBoundary, bottomRightBoundary);
	}

	resizeThroughCenter(vector: IVector<TCoordinateSpace>): IRegion<TCoordinateSpace> {
		if (vector.x === 0 && vector.y === 0) return this as any as IRegion<TCoordinateSpace>;

		const { topLeft, bottomRight, size, topLeftBoundary, bottomRightBoundary } = this;

		let clampedTopLeftVector = this.coordinateSpace.createVector(
			clamp(-vector.x / 2, topLeftBoundary.x - topLeft.x, size.width / 2),
			clamp(-vector.y / 2, topLeftBoundary.y - topLeft.y, size.height / 2),
		);
		let clampedBottomRightVector = this.coordinateSpace.createVector(
			clamp(vector.x / 2, -size.width / 2, bottomRightBoundary.x - bottomRight.x),
			clamp(vector.y / 2, -size.height / 2, bottomRightBoundary.y - bottomRight.y),
		);
		const clampedTopLeftVectorLength = clampedTopLeftVector.length();
		const clampedBottomRightVectorLength = clampedTopLeftVector.length();
		if (clampedTopLeftVectorLength !== clampedBottomRightVectorLength) {
			if (clampedTopLeftVectorLength < clampedBottomRightVectorLength) {
				clampedBottomRightVector = clampedTopLeftVector.negate();
			} else {
				clampedTopLeftVector = clampedBottomRightVector.negate();
			}
		}

		const newTopLeft = topLeft.move(clampedTopLeftVector);
		const newBottomRight = bottomRight.move(clampedBottomRightVector);

		return this.coordinateSpace.createRegion(newTopLeft, newBottomRight, topLeftBoundary, bottomRightBoundary);
	}

	ceil(): IRegion<TCoordinateSpace> {
		const { coordinateSpace, topLeft, bottomRight, topLeftBoundary, bottomRightBoundary } = this;

		// Round boundaries too so coordinates cannot exceed rounded boundaries
		const topLeftBoundaryX = Math.ceil(topLeftBoundary.x);
		const topLeftBoundaryY = Math.ceil(topLeftBoundary.y);
		const bottomRightBoundaryX = Math.floor(bottomRightBoundary.x);
		const bottomRightBoundaryY = Math.floor(bottomRightBoundary.y);

		if (topLeftBoundaryX > bottomRightBoundaryX) {
			throw new Error("Cannot round this region because it is too narrow");
		}

		if (topLeftBoundaryY > bottomRightBoundaryY) {
			throw new Error("Cannot round this region because it is too small");
		}

		// Round & clamp
		const topLeftX = clamp(Math.ceil(topLeft.x), topLeftBoundaryX, bottomRightBoundaryX);
		const topLeftY = clamp(Math.ceil(topLeft.y), topLeftBoundaryY, bottomRightBoundaryY);
		const bottomRightX = clamp(Math.ceil(bottomRight.x), topLeftBoundaryX, bottomRightBoundaryX);
		const bottomRightY = clamp(Math.ceil(bottomRight.y), topLeftBoundaryY, bottomRightBoundaryY);

		// Don't allocate an object if we don't need to
		if (topLeft.x === topLeftX && topLeft.y === topLeftY && bottomRight.x === bottomRightX && bottomRight.y === bottomRightY) {
			return this;
		}

		const newTopLeft = coordinateSpace.createPoint(topLeftX, topLeftY);
		const newBottomRight = coordinateSpace.createPoint(bottomRightX, bottomRightY);
		return coordinateSpace.createRegion(newTopLeft, newBottomRight, topLeftBoundary, bottomRightBoundary);
	}

	equals(other: IRegion<TCoordinateSpace>) {
		return (
			this.kind === other.kind &&
			this.topLeft.equals(other.topLeft) &&
			this.bottomRight.equals(other.bottomRight) &&
			this.topLeftBoundary.equals(other.topLeftBoundary) &&
			this.bottomRightBoundary.equals(other.bottomRightBoundary)
		);
	}

	transform(transformation: ITransformation<TCoordinateSpace>): IRegion<InTransformed<TCoordinateSpace>> {
		if (!transformation) throw new Error("❌ Tried to transform a region but the 'transformation' parameter is null or undefined");

		const transformedPoints = [
			this.topLeft.transform(transformation),
			this.topRight.transform(transformation),
			this.bottomLeft.transform(transformation),
			this.bottomRight.transform(transformation),
		];

		transformedPoints.sort(sortTopThenLeft);

		const transformedBoundaryPoints = [
			this.topLeftBoundary.transform(transformation),
			this.topRightBoundary.transform(transformation),
			this.bottomLeftBoundary.transform(transformation),
			this.bottomRightBoundary.transform(transformation),
		];

		transformedBoundaryPoints.sort(sortTopThenLeft);

		const transformedTopLeft = transformedPoints[0];
		const transformedBottomRight = transformedPoints[3];
		const transformedTopLeftBoundary = transformedBoundaryPoints[0];
		const transformedBottomRightBoundary = transformedBoundaryPoints[3];

		return new RegionInTransformed<TCoordinateSpace>(
			this.coordinateSpace,
			transformation,
			transformedTopLeft,
			transformedBottomRight,
			transformedTopLeftBoundary,
			transformedBottomRightBoundary,
		);
	}

	toBasicRegion(): BasicRegion {
		return {
			topLeft: this.topLeft.toBasicPoint(),
			bottomRight: this.bottomRight.toBasicPoint(),
			topLeftBoundary: this.topLeftBoundary.toBasicPoint(),
			bottomRightBoundary: this.bottomRightBoundary.toBasicPoint(),
		};
	}

	toString() {
		const { topLeft, topRight, bottomLeft, bottomRight, size } = this;

		const paddingLength = 12;
		const x1 = topLeft.toString().padStart(paddingLength);
		const x2 = topRight.toString();
		const padding = "".padStart(paddingLength);
		const x3 = bottomLeft.toString().padStart(paddingLength);
		const x4 = bottomRight.toString();

		// @formatter:off
		return (
			"\n" +
			`${padding} Region ${size}\n` +
			`${x1}*------------------* ${x2}\n` +
			`${padding}|                  |\n` +
			`${padding}|                  |\n` +
			`${padding}|                  |\n` +
			`${x3}*------------------* ${x4}`
		);
		// @formatter:on
	}

	private validate(): void {
		const { topLeft, bottomRight, topLeftBoundary, bottomRightBoundary } = this;

		if (topLeft.x < topLeftBoundary.x) throw new Error(`Invalid region because topLeft.x < topLeftBoundary.x : ${this}`);

		if (topLeft.y < topLeftBoundary.y) throw new Error(`Invalid region because topLeft.y < topLeftBoundary.y : ${this}`);

		if (topLeft.x > bottomRight.x) throw new Error(`Invalid region because topLeft.x > bottomRight.x : ${this}`);

		if (topLeft.y > bottomRight.y) throw new Error(`Invalid region because topLeft.y > bottomRight.y : ${this}`);

		if (bottomRight.x > bottomRightBoundary.x) throw new Error(`Invalid region because bottomRight.x > bottomRightBoundary.x: ${this}`);

		if (bottomRight.y > bottomRightBoundary.y) throw new Error(`Invalid region because bottomRight.y > bottomRightBoundary.y: ${this}`);

		if (bottomRight.y - topLeft.y === 0) console.warn("Created a region with zero height!");

		if (bottomRight.x - topLeft.x === 0) console.warn("Created a region with zero width!");
	}

	static readonly fromTopLeftAndBottomRightAndMaxSize = <TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(coordinateSpace: ICoordinateSpace<TCoordinateSpace>) => {
		return function (topLeft: IPoint<TCoordinateSpace>, bottomRight: IPoint<TCoordinateSpace>, maxSize: ISize<any>): IRegion<TCoordinateSpace> {
			const topLeftBoundary = coordinateSpace.createPoint(0, 0);
			const bottomRightBoundary = coordinateSpace.createPoint(maxSize.width, maxSize.height);

			if (topLeft.x <= bottomRight.x && topLeft.y <= bottomRight.y) {
				return coordinateSpace.createRegion(topLeft, bottomRight, topLeftBoundary, bottomRightBoundary);
			}

			const adjustedTopLeft = coordinateSpace.createPoint(Math.min(topLeft.x, bottomRight.x), Math.min(topLeft.y, bottomRight.y));
			const adjustedBottomRight = coordinateSpace.createPoint(Math.max(topLeft.x, bottomRight.x), Math.max(topLeft.y, bottomRight.y));
			return coordinateSpace.createRegion(adjustedTopLeft, adjustedBottomRight, topLeftBoundary, bottomRightBoundary);
		};
	};

	static readonly fromTopLeftAndBottomRight = <TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(coordinateSpace: ICoordinateSpace<TCoordinateSpace>) => {
		return function (topLeft: IPoint<TCoordinateSpace>, bottomRight: IPoint<TCoordinateSpace>): IRegion<TCoordinateSpace> {
			const topLeftBoundary = coordinateSpace.createPoint(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
			const bottomRightBoundary = coordinateSpace.createPoint(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);

			if (topLeft.x <= bottomRight.x && topLeft.y <= bottomRight.y) {
				return coordinateSpace.createRegion(topLeft, bottomRight, topLeftBoundary, bottomRightBoundary);
			}

			const adjustedTopLeft = coordinateSpace.createPoint(Math.min(topLeft.x, bottomRight.x), Math.min(topLeft.y, bottomRight.y));
			const adjustedBottomRight = coordinateSpace.createPoint(Math.max(topLeft.x, bottomRight.x), Math.max(topLeft.y, bottomRight.y));
			return coordinateSpace.createRegion(adjustedTopLeft, adjustedBottomRight, topLeftBoundary, bottomRightBoundary);
		};
	};

	static readonly fromTopLeftAndSize = <TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(coordinateSpace: ICoordinateSpace<TCoordinateSpace>) => {
		return function (topLeft: IPoint<TCoordinateSpace>, size: ISize<TCoordinateSpace>): IRegion<TCoordinateSpace> {
			const bottomRight = coordinateSpace.createPoint(topLeft.x + size.width, topLeft.y + size.height);

			return Region.fromTopLeftAndBottomRight(coordinateSpace)(topLeft, bottomRight);
		};
	};

	static readonly fromTopLeftAndSizeAndMaxSize = <TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(coordinateSpace: ICoordinateSpace<TCoordinateSpace>) => {
		return function (topLeft: IPoint<TCoordinateSpace>, size: ISize<TCoordinateSpace>, maxSize: ISize<any>): IRegion<TCoordinateSpace> {
			const bottomRight = coordinateSpace.createPoint(topLeft.x + size.width, topLeft.y + size.height);
			return coordinateSpace.createRegion(topLeft, bottomRight, coordinateSpace.createPoint(0, 0), coordinateSpace.createPoint(maxSize.width, maxSize.height));
		};
	};

	static readonly fromSizeAndMaxSize = <TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(coordinateSpace: ICoordinateSpace<TCoordinateSpace>) => {
		return function (size: ISize<TCoordinateSpace>, maxSize: ISize<any>): IRegion<TCoordinateSpace> {
			const topLeft = coordinateSpace.createPoint(0, 0);
			const bottomRight = coordinateSpace.createPoint(size.width, size.height);
			return coordinateSpace.createRegion(topLeft, bottomRight, topLeft, coordinateSpace.createPoint(maxSize.width, maxSize.height));
		};
	};

	static readonly fromBoundingPoints = <TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(coordinateSpace: ICoordinateSpace<TCoordinateSpace>) => {
		return function (boundingPoints: IPoint<TCoordinateSpace>[], maxSize?: ISize<TCoordinateSpace>): IRegion<TCoordinateSpace> {
			if (boundingPoints.length < 2) throw new Error("Invalid argument boundingPoints: need at least 2 boundingPoints to create a bounding region");

			// noinspection ES6DestructuringVariablesMerge
			let { x: topLeftX, y: topLeftY, x: bottomRightX, y: bottomRightY } = boundingPoints[0];
			for (let i = 1; i < boundingPoints.length; i++) {
				const boundingPoint = boundingPoints[i];
				topLeftX = Math.min(topLeftX, boundingPoint.x);
				topLeftY = Math.min(topLeftY, boundingPoint.y);
				bottomRightX = Math.max(bottomRightX, boundingPoint.x);
				bottomRightY = Math.max(bottomRightY, boundingPoint.y);
			}

			const topLeftPoint = coordinateSpace.createPoint(topLeftX, topLeftY);
			const bottomRightPoint = coordinateSpace.createPoint(bottomRightX, bottomRightY);
			const topLeftBoundary = coordinateSpace.createPoint(0, 0);
			const bottomRightBoundary = coordinateSpace.createPoint(maxSize?.width ?? bottomRightX, maxSize?.height ?? bottomRightY);

			return coordinateSpace.createRegion(topLeftPoint, bottomRightPoint, topLeftBoundary, bottomRightBoundary);
		};
	};

	static readonly fromBoundingPointsWithExpandableBoundaries = <TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(
		coordinateSpace: ICoordinateSpace<TCoordinateSpace>,
	) => {
		return function (boundingPoints: IPoint<TCoordinateSpace>[], maxSize: ISize<TCoordinateSpace>): IRegion<TCoordinateSpace> {
			if (boundingPoints.length < 2) throw new Error("Invalid argument boundingPoints: need at least 2 boundingPoints to create a bounding region");

			// noinspection ES6DestructuringVariablesMerge
			let { x: topLeftX, y: topLeftY, x: bottomRightX, y: bottomRightY } = boundingPoints[0];
			for (let i = 1; i < boundingPoints.length; i++) {
				const boundingPoint = boundingPoints[i];
				topLeftX = Math.min(topLeftX, boundingPoint.x);
				topLeftY = Math.min(topLeftY, boundingPoint.y);
				bottomRightX = Math.max(bottomRightX, boundingPoint.x);
				bottomRightY = Math.max(bottomRightY, boundingPoint.y);
			}

			const topLeftPoint = coordinateSpace.createPoint(topLeftX, topLeftY);
			const bottomRightPoint = coordinateSpace.createPoint(bottomRightX, bottomRightY);
			const topLeftBoundary = coordinateSpace.createPoint(topLeftX < 0 ? topLeftX : 0, topLeftY < 0 ? topLeftY : 0);
			const bottomRightBoundary = coordinateSpace.createPoint(
				bottomRightX > maxSize.width ? bottomRightX : maxSize.width,
				bottomRightY > maxSize.height ? bottomRightY : maxSize.height,
			);

			return coordinateSpace.createRegion(topLeftPoint, bottomRightPoint, topLeftBoundary, bottomRightBoundary);
		};
	};

	static fromBasicRegion<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(basicRegion: BasicRegion, coordinateSpace: TCoordinateSpace) {
		const topLeft = Point.fromBasicPoint(basicRegion.topLeft, coordinateSpace);
		const bottomRight = Point.fromBasicPoint(basicRegion.bottomRight, coordinateSpace);
		const topLeftBoundary = Point.fromBasicPoint(basicRegion.topLeftBoundary, coordinateSpace);
		const bottomRightBoundary = Point.fromBasicPoint(basicRegion.bottomRightBoundary, coordinateSpace);
		return coordinateSpace.createRegion(topLeft, bottomRight, topLeftBoundary, bottomRightBoundary);
	}
}

export interface IRegionInWindow extends IRegion<InWindow> {}

export interface IRegionInVisibleCanvas extends IRegion<InVisibleCanvas> {}

export interface IRegionInVisibleCanvasDrawRegion extends IRegion<InVisibleCanvasDrawRegion> {}

export interface IRegionInOverviewImage extends IRegion<InOverviewImage> {}

export interface IRegionInFullSizeImage extends IRegion<InFullSizeImage> {}

export interface IRegionInTransformedFullSizeImage extends IRegion<InTransformed<InFullSizeImage>> {}

export interface IRegionInTransformedOverviewImage extends IRegion<InTransformed<InOverviewImage>> {}

export class RegionInWindow extends Region<InWindow> implements IRegionInWindow {
	public readonly kind: string = "region-in-window";

	constructor(topLeft: IPoint<InWindow>, bottomRight: IPoint<InWindow>, topLeftBoundary?: IPoint<InWindow>, bottomRightBoundary?: IPoint<InWindow>) {
		super(
			CoordinateSpace.inWindow,
			topLeft,
			bottomRight,
			topLeftBoundary ?? new PointInWindow(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY),
			bottomRightBoundary ?? new PointInWindow(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY),
		);
	}
}

export class RegionInVisibleCanvas extends Region<InVisibleCanvas> implements IRegionInVisibleCanvas {
	public readonly kind: string = "region-in-visible-canvas";

	constructor(topLeft: IPoint<InVisibleCanvas>, bottomRight: IPoint<InVisibleCanvas>, topLeftBoundary: IPoint<InVisibleCanvas>, bottomRightBoundary: IPoint<InVisibleCanvas>) {
		super(CoordinateSpace.inVisibleCanvas, topLeft, bottomRight, topLeftBoundary, bottomRightBoundary);
	}
}

export class RegionInVisibleCanvasDrawRegion extends Region<InVisibleCanvasDrawRegion> implements IRegionInVisibleCanvasDrawRegion {
	public readonly kind: string = "region-in-visible-canvas-draw-region";

	constructor(
		topLeft: IPoint<InVisibleCanvasDrawRegion>,
		bottomRight: IPoint<InVisibleCanvasDrawRegion>,
		topLeftBoundary: IPoint<InVisibleCanvasDrawRegion>,
		bottomRightBoundary: IPoint<InVisibleCanvasDrawRegion>,
	) {
		super(CoordinateSpace.inVisibleCanvasDrawRegion, topLeft, bottomRight, topLeftBoundary, bottomRightBoundary);
	}
}

export class RegionInOverviewImage extends Region<InOverviewImage> implements IRegionInOverviewImage {
	public readonly kind: string = "region-in-overview-image";

	constructor(topLeft: IPoint<InOverviewImage>, bottomRight: IPoint<InOverviewImage>, topLeftBoundary: IPoint<InOverviewImage>, bottomRightBoundary: IPoint<InOverviewImage>) {
		super(CoordinateSpace.inOverviewImage, topLeft, bottomRight, topLeftBoundary, bottomRightBoundary);
	}
}

export class RegionInFullSizeImage extends Region<InFullSizeImage> implements IRegionInFullSizeImage {
	public readonly kind: string = "region-in-full-size-image";

	constructor(topLeft: IPoint<InFullSizeImage>, bottomRight: IPoint<InFullSizeImage>, topLeftBoundary?: IPoint<InFullSizeImage>, bottomRightBoundary?: IPoint<InFullSizeImage>) {
		super(
			CoordinateSpace.inFullSizeImage,
			topLeft,
			bottomRight,
			topLeftBoundary ?? new PointInFullSizeImage(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY),
			bottomRightBoundary ?? new PointInFullSizeImage(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY),
		);
	}
}

export class RegionInTransformed<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> extends Region<InTransformed<TCoordinateSpace>> {
	readonly kind = "region-in-transformed" as const;

	constructor(
		private readonly originalCoordinateSpace: ICoordinateSpace<TCoordinateSpace>,
		transformation: ITransformation<TCoordinateSpace>,
		topLeft: IPoint<InTransformed<TCoordinateSpace>>,
		bottomRight: IPoint<InTransformed<TCoordinateSpace>>,
		topLeftBoundary: IPoint<InTransformed<TCoordinateSpace>>,
		bottomRightBoundary: IPoint<InTransformed<TCoordinateSpace>>,
	) {
		super(CoordinateSpace.inTransformed(originalCoordinateSpace, transformation), topLeft, bottomRight, topLeftBoundary, bottomRightBoundary);
	}
}

export const transformRegionToOriginal =
	<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(coordinateSpace: InTransformed<TCoordinateSpace>) =>
	(region: IRegion<InTransformed<TCoordinateSpace>>) => {
		const { topLeft, topRight, bottomRight, bottomLeft, topLeftBoundary, topRightBoundary, bottomRightBoundary, bottomLeftBoundary } = region;
		const { originalCoordinateSpace } = coordinateSpace;
		const transformedPoints = [
			coordinateSpace.transformToOriginal(topLeft),
			coordinateSpace.transformToOriginal(topRight),
			coordinateSpace.transformToOriginal(bottomLeft),
			coordinateSpace.transformToOriginal(bottomRight),
		];

		transformedPoints.sort(sortTopThenLeft);

		const transformedBoundaryPoints = [
			coordinateSpace.transformToOriginal(topLeftBoundary),
			coordinateSpace.transformToOriginal(topRightBoundary),
			coordinateSpace.transformToOriginal(bottomLeftBoundary),
			coordinateSpace.transformToOriginal(bottomRightBoundary),
		];

		transformedBoundaryPoints.sort(sortTopThenLeft);

		const transformedTopLeft = transformedPoints[0];
		const transformedBottomRight = transformedPoints[3];
		const transformedTopLeftBoundary = transformedBoundaryPoints[0];
		const transformedBottomRightBoundary = transformedBoundaryPoints[3];

		return originalCoordinateSpace.createRegion(transformedTopLeft, transformedBottomRight, transformedTopLeftBoundary, transformedBottomRightBoundary);
	};

export class RegionInTransformedFullSizeImage extends RegionInTransformed<InFullSizeImage> implements IRegionInTransformedFullSizeImage {
	constructor(
		transformation: ITransformation<InFullSizeImage>,
		topLeft: IPoint<InTransformed<InFullSizeImage>>,
		bottomRight: IPoint<InTransformed<InFullSizeImage>>,
		topLeftBoundary?: IPoint<InTransformed<InFullSizeImage>>,
		bottomRightBoundary?: IPoint<InTransformed<InFullSizeImage>>,
	) {
		super(
			CoordinateSpace.inFullSizeImage,
			transformation,
			topLeft,
			bottomRight,
			topLeftBoundary ?? new PointInTransformedFullSizeImage(transformation, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY),
			bottomRightBoundary ?? new PointInTransformedFullSizeImage(transformation, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY),
		);
	}
}

export const regionInFullSizeImageToRegionInTransformedFullSizeImage =
	() =>
	(transformation: ITransformation<InFullSizeImage>, region: IRegionInFullSizeImage): IRegionInTransformedFullSizeImage => {
		return region.transform(transformation);
	};

/**
 * When zooming in (or out), we can possibly modify the aspect ratio of the image region to better fit the canvas.
 * For example, when the zoomed out overview image is height bound, i.e. there are black bars to the left and right of the image
 * and we zoom in, we can chip away at those black bars and put some more pixels there.
 */
export const adjustAspectRatio =
	(visibleCanvasStore: IVisibleCanvasStore) =>
	<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(region: IRegion<TCoordinateSpace>): IRegion<TCoordinateSpace> => {
		const { size: visibleCanvasSize } = visibleCanvasStore;

		if (!visibleCanvasSize) return region;

		console.debug("📐 Zoomed, adjusting aspect ratio of full size image region to possibly make better use of available canvas surface");
		const { size: imageRegionSize, coordinateSpace } = region;
		const aspectRatioDifference = Math.abs(imageRegionSize.aspectRatio - visibleCanvasSize.aspectRatio);

		// Aspect ratio is already perfect
		if (aspectRatioDifference <= 0.01) {
			console.debug("📐 Aspect ratio is already perfect");
			return region;
		}

		// If the visible canvas size has a height of 0px, its aspect ratio will also be 0. Since we cannot divide by zero, we must short circuit here
		if (visibleCanvasSize.aspectRatio === 0) {
			console.debug("📐 Visible canvas aspect ratio is zero, cannot adjust region");
			return region;
		}
		const aspectRatioFactor = imageRegionSize.aspectRatio / visibleCanvasSize.aspectRatio;
		const imageRegionSizeAdjustedToAspectRatio =
			visibleCanvasSize.aspectRatio > imageRegionSize.aspectRatio
				? coordinateSpace.createSize(Math.max(imageRegionSize.width / aspectRatioFactor, 1), imageRegionSize.height)
				: coordinateSpace.createSize(imageRegionSize.width, Math.max(imageRegionSize.height * aspectRatioFactor, 1));

		console.debug(`📐 Changing full size image region to ${imageRegionSizeAdjustedToAspectRatio}`);

		return region.zoomTo(imageRegionSizeAdjustedToAspectRatio);
	};

export const regionInFullImageToRegionInOverviewImage = (overviewImageSize: ISize<InOverviewImage>) => (region: IRegionInFullSizeImage) => {
	const { topLeft, bottomRight, maxSize: fullImageSize } = region;
	const ratio = overviewImageSize.width / fullImageSize.width;
	const overviewTopLeft = new PointInOverviewImage(
		clamp(Math.round(ratio * topLeft.x), 0, overviewImageSize.width),
		clamp(Math.round(ratio * topLeft.y), 0, overviewImageSize.height),
	);
	const overviewBottomRight = new PointInOverviewImage(
		clamp(Math.round(ratio * bottomRight.x), 0, overviewImageSize.width),
		clamp(Math.round(ratio * bottomRight.y), 0, overviewImageSize.height),
	);
	return Region.fromTopLeftAndBottomRightAndMaxSize(CoordinateSpace.inOverviewImage)(overviewTopLeft, overviewBottomRight, overviewImageSize);
};
